/* Proprietary - see LICENSE */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl, buildAuthHeaders } from '../config/api';
import apiClient from '../config/apiClient';

// ─── Helpers ────────────────────────────────────────────────────

const authFetch = async (path, options = {}) => {
  const res = await fetch(apiUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthHeaders(),
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    const err = new Error(body || `Request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  // 204 No Content or empty body (e.g. void DELETE responses return 200 + empty body)
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const jsonBody = (data) => ({ body: JSON.stringify(data) });

// ─── Query-key factories ────────────────────────────────────────

export const queryKeys = {
  frameworks: ['frameworks'],
  tasks: ['tasks'],
  risks: ['risks'],
  assets: ['assets'],
  incidents: ['incidents'],
  users: ['users'],
  documents: ['documents'],
  folders: ['folders'],
  groups: ['groups'],
  whistleblower: ['whistleblower'],
  whistleblowerStats: ['whistleblower', 'statistics'],
  reports: (type) => ['reports', type],
  dashboard: ['dashboard'],
  aiSystems: ['ai-systems'],
};

// ─── Dashboard ──────────────────────────────────────────────────

export function useDashboardData() {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: async () => {
      const [frameworks, tasks, risks, assets, incidents] = await Promise.all([
        authFetch('/frameworks').catch(() => []),
        authFetch('/tasks').catch(() => []),
        authFetch('/risks').catch(() => []),
        authFetch('/assets').catch(() => []),
        authFetch('/incidents').catch(() => []),
      ]);
      return {
        frameworks: Array.isArray(frameworks) ? frameworks : [],
        tasks: Array.isArray(tasks) ? tasks : [],
        risks: Array.isArray(risks) ? risks : [],
        assets: Array.isArray(assets) ? assets : [],
        incidents: Array.isArray(incidents) ? incidents : [],
      };
    },
    staleTime: 30_000, // 30 s
  });
}

// ─── Frameworks ─────────────────────────────────────────────────

export function useFrameworks() {
  return useQuery({
    queryKey: queryKeys.frameworks,
    queryFn: () => authFetch('/frameworks'),
  });
}

/**
 * Framework types this tenant may activate. `allowed: null` means
 * unrestricted — the tenant has no subscription limit configured.
 */
export function useAllowedFrameworks() {
  return useQuery({
    queryKey: [...queryKeys.frameworks, 'allowed'],
    queryFn: () => authFetch('/frameworks/allowed'),
  });
}

/**
 * True when the tenant has this framework active. Use it to gate any menu
 * entry or page that only makes sense for one framework, so the feature
 * appears with the framework instead of having to be remembered separately.
 *
 *   const hasCra = useHasFramework('CRA');
 */
export function useHasFramework(type) {
  const { data: frameworks = [] } = useFrameworks();
  return frameworks.some((f) => f.type === type && f.isActive !== false);
}

export function useCreateFramework() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/frameworks', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.frameworks });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useUpdateFramework() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/frameworks/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.frameworks });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

// ─── Tasks ──────────────────────────────────────────────────────

export function useTasks() {
  return useQuery({
    queryKey: queryKeys.tasks,
    queryFn: async () => {
      const res = await apiClient.get('/tasks');
      return res.data;
    },
  });
}

export function useActiveFrameworks() {
  return useQuery({
    queryKey: [...queryKeys.frameworks, 'active'],
    queryFn: async () => {
      const res = await apiClient.get('/frameworks');
      return res.data.filter((f) => f.isActive);
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/tasks/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tasks });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useCreateCustomTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/tasks/custom', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tasks });
      qc.invalidateQueries({ queryKey: queryKeys.frameworks });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

// ─── Risks ──────────────────────────────────────────────────────

export function useRisks() {
  return useQuery({
    queryKey: queryKeys.risks,
    queryFn: () => authFetch('/risks'),
  });
}

export function useCreateRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/risks', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.risks });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useUpdateRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/risks/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.risks });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useDeleteRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/risks/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.risks });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

// ─── Assets ─────────────────────────────────────────────────────

export function useAssets() {
  return useQuery({
    queryKey: queryKeys.assets,
    queryFn: () => authFetch('/assets'),
  });
}

export function useAsset(id) {
  return useQuery({
    queryKey: [...queryKeys.assets, id],
    queryFn: () => authFetch(`/assets/${id}`),
    enabled: !!id,
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/assets', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.assets });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/assets/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.assets });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/assets/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.assets });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

// ─── AI systems (AI Act register) ───────────────────────────────

export function useAiSystems(enabled = true) {
  return useQuery({
    queryKey: queryKeys.aiSystems,
    queryFn: () => authFetch('/ai-systems'),
    enabled,
  });
}

export function useAiSystemObligations(id) {
  return useQuery({
    queryKey: [...queryKeys.aiSystems, id, 'obligations'],
    queryFn: () => authFetch(`/ai-systems/${id}/obligations`),
    enabled: !!id,
  });
}

export function useAiWizard(enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.aiSystems, 'wizard'],
    queryFn: () => authFetch('/ai-systems/wizard'),
    enabled,
    staleTime: Infinity,
  });
}

export function useCreateAiSystem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/ai-systems', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.aiSystems }),
  });
}

export function useUpdateAiSystem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/ai-systems/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.aiSystems }),
  });
}

export function useDeleteAiSystem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/ai-systems/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.aiSystems }),
  });
}

/** Runs the rules without saving, so the wizard can show the outcome live. */
export function usePreviewClassification() {
  return useMutation({
    mutationFn: (answers) =>
      authFetch('/ai-systems/wizard/preview', { method: 'POST', ...jsonBody({ answers }) }),
  });
}

export function useClassifyAiSystem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, answers }) =>
      authFetch(`/ai-systems/${id}/classify`, { method: 'POST', ...jsonBody({ answers }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.aiSystems }),
  });
}

export function useGenerateAiTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/ai-systems/${id}/generate-tasks`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.aiSystems });
      qc.invalidateQueries({ queryKey: queryKeys.tasks });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

// ─── Incidents ──────────────────────────────────────────────────

export function useIncidents() {
  return useQuery({
    queryKey: queryKeys.incidents,
    queryFn: () => authFetch('/incidents'),
  });
}

export function useCreateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/incidents', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.incidents });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useUpdateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/incidents/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.incidents });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useDeleteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/incidents/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.incidents });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

// ─── Reports ────────────────────────────────────────────────────

export function useReport(type) {
  return useQuery({
    queryKey: queryKeys.reports(type),
    queryFn: () => authFetch(`/reports/${type}`),
    enabled: false, // only fetch on demand
  });
}

/** Audit report for one framework: every requirement with its evidence. */
export function useFrameworkAuditReport(frameworkId) {
  return useQuery({
    queryKey: queryKeys.reports(`framework:${frameworkId}`),
    queryFn: () => authFetch(`/reports/framework/${frameworkId}`),
    enabled: false, // only fetch on demand
  });
}

// ─── Users ──────────────────────────────────────────────────────

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: () => authFetch('/users'),
    enabled,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/users', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/users/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users }),
  });
}

// ─── Documents ──────────────────────────────────────────────────

export function useDocuments() {
  return useQuery({
    queryKey: queryKeys.documents,
    queryFn: () => authFetch('/documents'),
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/documents', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents });
      qc.invalidateQueries({ queryKey: queryKeys.folders });
    },
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/documents/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents });
      qc.invalidateQueries({ queryKey: queryKeys.folders });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents });
      qc.invalidateQueries({ queryKey: queryKeys.folders });
    },
  });
}

// ─── Document folders ───────────────────────────────────────────

export function useFolders() {
  return useQuery({
    queryKey: queryKeys.folders,
    queryFn: () => authFetch('/folders'),
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/folders', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.folders }),
  });
}

export function useUpdateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/folders/${id}`, { method: 'PATCH', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.folders }),
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/folders/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.folders }),
  });
}

export function useMoveDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/folders/documents', { method: 'PATCH', ...jsonBody(data) }),
    // Both lists change: the documents move, and the per-folder counts shift.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.documents });
      qc.invalidateQueries({ queryKey: queryKeys.folders });
    },
  });
}

/**
 * The organisation the user is currently working in, for branding.
 *
 * Read through GET /tenants/:id, which any user may call for their own
 * tenant. The tenant switcher's list is not a substitute: it is only loaded
 * for owners, super admins and multi-tenant users, so a customer's own staff
 * would never see their organisation's logo.
 *
 * Cached for five minutes — a logo changes about once a year, and this runs on
 * every page.
 */
export function useCurrentTenant() {
  let tenantId = null;
  try {
    tenantId =
      sessionStorage.getItem('selectedTenantId') ||
      JSON.parse(sessionStorage.getItem('user') || '{}').tenantId ||
      null;
  } catch {
    tenantId = null;
  }

  return useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => authFetch(`/tenants/${tenantId}`),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useFolderPermissions(folderId) {
  return useQuery({
    queryKey: [...queryKeys.folders, folderId, 'permissions'],
    queryFn: () => authFetch(`/folders/${folderId}/permissions`),
    enabled: !!folderId,
  });
}

export function useSetFolderPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) =>
      authFetch(`/folders/${id}/permissions`, { method: 'PUT', ...jsonBody(data) }),
    // Changing a grant changes what the current user can see too, so the
    // folder tree and document list have to be refetched, not just the dialog.
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...queryKeys.folders, id, 'permissions'] });
      qc.invalidateQueries({ queryKey: queryKeys.folders });
      qc.invalidateQueries({ queryKey: queryKeys.documents });
    },
  });
}

// ─── Groups ─────────────────────────────────────────────────────

export function useGroups(enabled = true) {
  return useQuery({
    queryKey: queryKeys.groups,
    queryFn: () => authFetch('/groups'),
    enabled,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/groups', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.groups }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/groups/${id}`, { method: 'PATCH', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.groups }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => authFetch(`/groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups });
      qc.invalidateQueries({ queryKey: queryKeys.folders });
    },
  });
}

export function useSetGroupMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userIds }) =>
      authFetch(`/groups/${id}/members`, { method: 'PUT', ...jsonBody({ userIds }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups });
      // Membership decides visibility, so both lists may look different now.
      qc.invalidateQueries({ queryKey: queryKeys.folders });
      qc.invalidateQueries({ queryKey: queryKeys.documents });
    },
  });
}

export function useUploadFile() {
  return useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append('files', file);
      const res = await fetch(apiUrl('/upload'), {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      return res.json();
    },
  });
}

export function useDeleteFile() {
  return useMutation({
    mutationFn: (fileId) => authFetch(`/upload/${fileId}`, { method: 'DELETE' }),
  });
}

// ─── Whistleblower ──────────────────────────────────────────────

export function useWhistleblowerReports(enabled = true) {
  return useQuery({
    queryKey: queryKeys.whistleblower,
    queryFn: () => authFetch('/whistleblower'),
    enabled,
  });
}

export function useWhistleblowerStats(enabled = true) {
  return useQuery({
    queryKey: queryKeys.whistleblowerStats,
    queryFn: () => authFetch('/whistleblower/statistics'),
    enabled,
  });
}

export function useCreateWhistleblowerReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => authFetch('/whistleblower', { method: 'POST', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.whistleblower });
      qc.invalidateQueries({ queryKey: queryKeys.whistleblowerStats });
    },
  });
}

export function useUpdateWhistleblowerReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch(`/whistleblower/${id}`, { method: 'PUT', ...jsonBody(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.whistleblower });
      qc.invalidateQueries({ queryKey: queryKeys.whistleblowerStats });
    },
  });
}

// ─── Upload multiple files (for whistleblower) ──────────────────

export function useUploadFiles() {
  return useMutation({
    mutationFn: async (files) => {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await fetch(apiUrl('/upload'), {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      return res.json();
    },
  });
}
// ─── Profile / Settings ─────────────────────────────────────

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => authFetch('/users/me'),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => authFetch('/users/me', { method: 'PATCH', ...jsonBody(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({ id, data }) => authFetch('/users/me/change-password', { method: 'POST', ...jsonBody(data) }),
  });
}
// ─── Tenant Settings ────────────────────────────────────────────

export function useTenantSettings() {
  return useQuery({
    queryKey: ['tenantSettings'],
    queryFn: () => authFetch('/tenants/settings'),
  });
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings) => authFetch('/tenants/settings', { method: 'PATCH', ...jsonBody(settings) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenantSettings'] }),
  });
}

// ─── AI ─────────────────────────────────────────────────────────

export function useAskAI() {
  return useMutation({
    // history: earlier turns as {role, content}, so follow-up questions make sense
    mutationFn: async ({ question, history = [] }) => {
      const res = await apiClient.post('/ai/ask', { question, history });
      return res.data;
    },
  });
}
